import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { authCodePolicy, config } from "../../lib/config";
import { authErrorMessage, requestAuthCode, verifyAuthCode } from "../../lib/cognito";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { MailIcon, CheckIcon, ClockIcon, RefreshIcon, ShieldIcon } from "../../components/ui/icons";
import { BrandMark } from "../../components/ui/brand-mark";
import { cn } from "../../lib/cn";
import { LostDeviceModal } from "./LostDeviceModal";

// g-03 Guide Sign In。Cognito CUSTOM_AUTH（email + 6桁コード / C-3-2）。
// 2フェーズ: ①email でコード送信 → ②6桁コードで検証。
// 仕様（10分有効・60秒で再送・5回まで）は authCodePolicy を単一の出所にする。
// サインイン後は /onboarding へ。/onboarding は guide.status で居場所を振り分ける状態別ランディング
// （未提出→ウィザード / 承認待ち→審査中 / Active→guide-app への橋 / それ以外→案内文・tst-00cu.5）。

type Phase = "email" | "code";

export function SignIn() {
  const { isAuthenticated, signIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const from = (location.state as { from?: Location } | null)?.from?.pathname ?? "/onboarding";

  const [phase, setPhase] = useState<Phase>("email");
  const [email, setEmail] = useState("");
  const [session, setSession] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [otpNonce, setOtpNonce] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState<number>(authCodePolicy.maxAttempts);
  const [resendIn, setResendIn] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLostDevice, setShowLostDevice] = useState(false);

  // 再送カウントダウン（60→0）。0 になったら再送可能。
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const sendCode = useCallback(async (addr: string) => {
    setBusy(true);
    setError(null);
    try {
      const { session: s } = await requestAuthCode(addr.trim().toLowerCase());
      setSession(s);
      setPhase("code");
      setCode("");
      setOtpNonce((n) => n + 1);
      setAttemptsLeft(authCodePolicy.maxAttempts);
      setResendIn(authCodePolicy.resendIntervalSeconds);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }, []);

  // codeOverride: OTP 完了時、state 反映前の確定値を直接渡す（closure の code はまだ古い）。
  const submitCode = useCallback(
    async (codeOverride?: string) => {
      const c = codeOverride ?? code;
      if (!session || c.length !== authCodePolicy.codeLength) return;
      setBusy(true);
      setError(null);
      try {
        const outcome = await verifyAuthCode(email.trim().toLowerCase(), c, session);
        if (outcome.status === "success") {
          signIn(outcome.idToken);
          navigate(from, { replace: true });
          return;
        }
        setSession(outcome.session);
        setCode("");
        setOtpNonce((n) => n + 1);
        setAttemptsLeft((n) => {
          const left = n - 1;
          setError(
            left > 0
              ? `Incorrect code. ${left} attempt${left === 1 ? "" : "s"} left.`
              : "No attempts left. Request a new code.",
          );
          return left;
        });
      } catch (err) {
        setError(authErrorMessage(err));
        setAttemptsLeft(0);
      } finally {
        setBusy(false);
      }
    },
    [session, code, email, signIn, navigate, from],
  );

  if (isAuthenticated) return <Navigate to={from} replace />;

  const exhausted = attemptsLeft <= 0;

  return (
    <div className="flex min-h-dvh flex-col bg-canvas px-5">
      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
        <div className="mb-2 flex items-center gap-2">
          <BrandMark className="size-5 text-ink" />
          <div className="eyebrow">Tokyo Signature Tours</div>
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-ink">Guide Sign In</h1>
        <p className="mt-2 text-sm text-muted">Enter the verification code sent to your email.</p>

        {/* フェーズ表示のピルトグル（g-03） */}
        <div className="mt-6 flex gap-2">
          <PhaseChip active={phase === "email"} done={phase === "code"} icon={MailIcon}>
            Email
          </PhaseChip>
          <PhaseChip active={phase === "code"} done={false} icon={ShieldIcon}>
            Verification code
          </PhaseChip>
        </div>

        <div className="mt-6 rounded-lg border border-line bg-paper p-5 shadow-card">
          {phase === "email" ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (email.trim()) void sendCode(email);
              }}
            >
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-ink-soft">
                Email
              </label>
              <div className="relative">
                <MailIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="guide@example.com"
                  className="h-12 w-full rounded-md border border-line bg-paper pl-9 pr-3 text-[15px] outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                />
              </div>
              {error && <p className="mt-3 text-sm text-danger">{error}</p>}
              <Button type="submit" size="block" className="mt-5" disabled={busy || !email.trim()}>
                {busy ? <Spinner className="text-white" /> : "Send code"}
              </Button>
            </form>
          ) : (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <span className="truncate text-sm text-muted">{email}</span>
                <button
                  type="button"
                  onClick={() => {
                    setPhase("email");
                    setError(null);
                  }}
                  className="shrink-0 text-sm font-medium text-accent hover:underline"
                >
                  Change email
                </button>
              </div>

              <label className="mb-2 block text-sm font-medium text-ink-soft">
                Verification code
              </label>
              <OtpInput
                key={otpNonce}
                value={code}
                onChange={(v) => {
                  setCode(v);
                  setError(null);
                }}
                onComplete={(full) => void submitCode(full)}
                disabled={busy || exhausted}
              />

              <ul className="mt-4 space-y-1.5 text-xs text-muted">
                <li className="flex items-center gap-2">
                  <ClockIcon className="size-3.5 text-faint" />
                  Code expires in {authCodePolicy.expiresMinutes} minutes.
                </li>
                <li className="flex items-center gap-2">
                  <RefreshIcon className="size-3.5 text-faint" />
                  {resendIn > 0
                    ? `You can request a new code in ${resendIn} seconds.`
                    : "You can request a new code now."}
                </li>
                <li className="flex items-center gap-2 text-warning">
                  <ShieldIcon className="size-3.5" />
                  {authCodePolicy.maxAttempts} attempts maximum.
                </li>
              </ul>

              {error && <p className="mt-3 text-sm text-danger">{error}</p>}

              <Button
                type="button"
                size="block"
                className="mt-5"
                onClick={() => void submitCode()}
                disabled={busy || exhausted || code.length !== authCodePolicy.codeLength}
              >
                {busy ? <Spinner className="text-white" /> : "Sign In"}
              </Button>

              <Button
                type="button"
                variant="outline"
                size="block"
                className="mt-2.5"
                onClick={() => void sendCode(email)}
                disabled={busy || (resendIn > 0 && !exhausted)}
              >
                {resendIn > 0 && !exhausted ? `Resend code (${resendIn}s)` : "Resend Code"}
              </Button>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-muted">
          Having trouble signing in?{" "}
          <a
            href={`mailto:${config.supportEmail}`}
            className="font-medium text-accent hover:underline"
          >
            Contact TST support
          </a>
        </p>
        <p className="mt-3 text-center text-sm text-muted">
          Lost or stolen device?{" "}
          <button
            type="button"
            onClick={() => setShowLostDevice(true)}
            className="font-medium text-accent hover:underline"
          >
            Report it here
          </button>
        </p>
        <p className="mt-3 text-center text-sm text-muted">
          New guide?{" "}
          <Link to="/apply" className="font-medium text-accent hover:underline">
            Apply here
          </Link>
        </p>
      </div>

      {showLostDevice && <LostDeviceModal email={email} onClose={() => setShowLostDevice(false)} />}
    </div>
  );
}

function PhaseChip({
  active,
  done,
  icon: Icon,
  children,
}: {
  active: boolean;
  done: boolean;
  icon: typeof MailIcon;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 text-sm font-medium",
        active
          ? "bg-ink text-white"
          : done
            ? "border border-line bg-paper text-ink"
            : "border border-line bg-paper text-faint",
      )}
    >
      {done ? <CheckIcon className="size-3.5" /> : <Icon className="size-3.5" />}
      {children}
    </span>
  );
}

// 6桁 OTP 入力。1桁ずつのボックス（g-03）。貼り付け・バックスペース・矢印に対応。
function OtpInput({
  value,
  onChange,
  onComplete,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onComplete: (full: string) => void;
  disabled?: boolean;
}) {
  const len = authCodePolicy.codeLength;
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.split("");
  const valueRef = useRef(value);

  const setAt = (i: number, d: string) => {
    const next = valueRef.current.split("");
    next[i] = d;
    const joined = next.join("").slice(0, len);
    valueRef.current = joined;
    onChange(joined);
    if (d && i < len - 1) refs.current[i + 1]?.focus();
    if (joined.length === len) onComplete(joined);
  };

  return (
    <div className="flex justify-between gap-2">
      {Array.from({ length: len }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          disabled={disabled}
          value={digits[i] ?? ""}
          onChange={(e) => {
            const d = e.target.value.replace(/\D/g, "").slice(-1);
            if (d) setAt(i, d);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace") {
              if (digits[i]) setAt(i, "");
              else if (i > 0) {
                refs.current[i - 1]?.focus();
                setAt(i - 1, "");
              }
            } else if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
            else if (e.key === "ArrowRight" && i < len - 1) refs.current[i + 1]?.focus();
          }}
          onPaste={(e) => {
            e.preventDefault();
            const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, len);
            if (pasted) {
              valueRef.current = pasted;
              onChange(pasted);
              if (pasted.length === len) onComplete(pasted);
              else refs.current[pasted.length]?.focus();
            }
          }}
          className="min-w-0 flex-1 rounded-md border border-line bg-paper text-center text-xl font-semibold text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 disabled:opacity-50"
          style={{ height: 56 }}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}
